import React, {Fragment, useEffect, useState, useCallback} from 'react';
import UserForm from './UserCreateForm';
import UserItem from './UserItem';
import {useDispatch, useSelector} from 'react-redux';
import {getUsers} from '../../actions/user';
import UserDeleteForm from "./UserDeleteForm";
import UserUpdateForm from "./UserUpdateForm";
import UserPermissionManager from "./UserPermissionManager";
import SearchForm from "../layout/SearchForm";
import {Plus} from "react-feather";
import CustomPaginationWithManyPages from "../layout/CustomPaginationWithManyPages";
import TableSpinner from "../layout/TableSpinner";
import { usePermission } from '../../hooks/usePermission';
import useActiveFirm from '../../hooks/useActiveFirm';

const Users = () => {
    const dispatch = useDispatch();
    const { userCan } = usePermission();
    const { firmId, firmName } = useActiveFirm();

    // Wrapper function to ensure firm_id is always included when calling getUsers
    // Returns a thunk function (async action) that can be dispatched
    const getUsersWithFirm = useCallback((page, params = {}) => {
        const filters = { ...params };
        if (firmId) {
            filters.firm_id = firmId;
        }
        // Clean up undefined/null values
        Object.keys(filters).forEach(key => {
            if (filters[key] === undefined || filters[key] === null || filters[key] === '' || filters[key] === 'undefined' || filters[key] === 'null') {
                delete filters[key];
            }
        });
        // Return the thunk function, don't dispatch it
        return getUsers(page, filters);
    }, [firmId]);

    // Fetch users when firm changes
    useEffect(() => {
        if (firmId) {
            dispatch(getUsersWithFirm(1, {}));
        }
    }, [dispatch, firmId, getUsersWithFirm]);

    const {
        users = [], loading, numberOfPages, currentPage, total_users, error
    } = useSelector(state => state.user);

    const [show, setShow] = useState(false);
    const [showPermissionManager, setShowPermissionManager] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    
    const handleShowPermissionManager = (user) => {
        setSelectedUser(user);
        setShowPermissionManager(true);
    };
    
    const handleClosePermissionManager = () => {
        setShowPermissionManager(false);
        setSelectedUser(null);
    };

    if (!userCan('view users')) {
        return <div>You don't have permission to view users.</div>;
    }

    const renderUserItems = () => {
        if (!Array.isArray(users)) {
            console.error('Users is not an array:', users);
            return (
                <tr>
                    <td colSpan="6" className="text-center">
                        Error: Invalid user data
                    </td>
                </tr>
            );
        }

        if (users.length === 0) {
            return (
                <tr>
                    <td colSpan="6" className="text-center">
                        No users found
                    </td>
                </tr>
            );
        }

        return users.map((item, index) => (
            <UserItem 
                key={item?.id || index} 
                number={index + 1} 
                user={item}
                onShowPermissionManager={handleShowPermissionManager}
            />
        ));
    };

    return (
        <Fragment>
            <div className="container-fluid p-0">
                <div className="row mb-2">
                    <div className="col-md-12 col-lg-8">
                        <h1 className="h3 ">
                            <strong>Tizimdagi</strong> foydalanuvchilar ({total_users || 0})
                            {firmName && <span className="text-muted ms-2" style={{fontSize: '14px', fontWeight: 'normal'}}>— {firmName}</span>}
                        </h1>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <SearchForm getPageItems={getUsersWithFirm}/>
                    </div>

                    <div className="col-md-6">
                        <div className="crud_actions text-end">
                            {userCan('create users') && (
                                <button className="btn btn-primary" onClick={handleShow}>
                                    Foydalanuvchi
                                    <Plus/>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Firma</th>
                                            <th scope="col">Ism</th>
                                            <th scope="col">Email</th>
                                            <th scope="col">Rol</th>
                                            <th scope="col">Amallar</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="6">
                                                        <TableSpinner/>
                                                    </td>
                                                </tr>
                                            ) : (
                                                renderUserItems()
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {numberOfPages > 1 && (
                    <CustomPaginationWithManyPages
                        numberOfPages={numberOfPages}
                        currentPage={currentPage}
                        getPageItems={getUsersWithFirm}
                    />
                )}
            </div>

            {userCan('create users') && (
                <UserForm show={show} handleClose={handleClose}/>
            )}
            <UserUpdateForm/>
            <UserDeleteForm/>
            <UserPermissionManager
                show={showPermissionManager}
                handleClose={handleClosePermissionManager}
                user={selectedUser}
            />
        </Fragment>
    );
};

export default Users;
